import { View } from '@play-co/timestep-core/ui';
import { toggleCheatsButtonVisibility } from 'src/game/components/shared/ButtonCheats';
import { devSettings } from 'src/lib/settings';
import ButtonScaleViewWithText, {
  ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import { GCInstant as platform } from 'src/lib/gcinstant';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import gsap from 'gsap';

const height = 350;

export class SettingDebugOverlay extends FlexBoxView {
  private readonly contentView: FlexBoxView;
  private readonly title: LangBitmapFontTextView;
  private readonly chevron: LangBitmapFontTextView;
  private isOpen = false;

  constructor(superview: View) {
    super({
      superview,
      flex: {
        width: superview.style.width,
        height: superview.style.height,
      },
      clip: true,
      canHandleEvents: false,
    });

    // Create content wrapper view
    this.contentView = new FlexBoxView({
      superview: this,
      flex: {
        position: 'absolute',
        width: '100%',
        height,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        bottom: 0,
      },
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    });

    // Create debug title
    this.title = new LangBitmapFontTextView({
      superview: this.contentView,
      width: superview.style.width,
      height: 60,
      font: 'Title',
      size: 25,
      align: 'center',
      verticalAlign: 'center',
      localeText: () => '-- DEBUG SETTINGS --',
      backgroundColor: '#00FF00',
    });
    this.title.onInputSelect = () => {
      this.setToggled(!this.isOpen);
    };

    // Create chevron icon
    this.chevron = new LangBitmapFontTextView({
      superview: this.title,
      width: this.title.style.height,
      height: this.title.style.height,
      font: 'Title',
      align: 'center',
      verticalAlign: 'center',
      size: 30,
      centerAnchor: true,
      offsetX: this.title.style.width - this.title.style.height - 10,
      localeText: () => '▼',
    });

    // Create cheats toggle button
    const cheatsButton = new ButtonScaleViewWithText({
      superview: this.contentView,
      ...uiConfig.buttons.primary,
      fontSize: 30,
      labelOffsetX: -7,
      labelOffsetY: -2,
      font: 'Body',
      width: 235,
      height: 75,
      localeText: () => `CHEATS ${devSettings.get('cheats') ? 'ON' : 'OFF'}`,
      onClick: async () => {
        const currentSettings = devSettings.load();
        const newValue = !currentSettings['cheats'];
        currentSettings['cheats'] = newValue;
        devSettings.save(currentSettings);
        toggleCheatsButtonVisibility(newValue);
        cheatsButton.label.localeText = () =>
          `CHEATS ${newValue ? 'ON' : 'OFF'}`;
      },
      tracking: { feature: 'cheats', $subFeature: 'cheats-toggle' },
    });

    const baseButtonOpts: Partial<ButtonScaleViewWithTextOpts> = {
      fontSize: 30,
      font: 'Title',
      align: 'center',
      verticalAlign: 'center',
      wordWrap: true,
      labelPaddingY: 10,
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
    };

    // Create user ID display clipboard button
    new ButtonScaleViewWithText({
      superview: this.contentView,
      width: superview.style.width,
      height: 75,
      ...baseButtonOpts,
      localeText: () => `UID: ${platform.playerID} 📋`,
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(platform.playerID);
          await toast('Copied User ID!');
        } catch (e) {
          await toast(`Copy failed: ${JSON.stringify(e)}`);
        }
      },
      tracking: {
        feature: 'dev',
        $subFeature: 'dev-copy-user-id',
      },
    });

    // Create app info display clipboard button
    const appInfo = `VERSION: ${process.env.APP_VERSION}\n(${process.env.GIT_BRANCH} / ${process.env.COMMITHASH}) 📋`;
    new ButtonScaleViewWithText({
      superview: this.contentView,
      width: superview.style.width,
      height: 100,
      ...baseButtonOpts,
      localeText: () => appInfo,
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(process.env.COMMITHASH!);
          await toast('Copied Version Info!');
        } catch (e) {
          await toast(`Copy failed: ${JSON.stringify(e)}`);
        }
      },
      tracking: {
        feature: 'dev',
        $subFeature: 'dev-copy-commit-hash',
      },
    });

    this.setToggled(false, true);
  }

  private async setToggled(value: boolean, instant = false) {
    this.isOpen = value;
    return gsap
      .timeline({ overwrite: true })
      .to(this.contentView.style, {
        duration: instant ? 0 : 0.2,
        offsetY: value ? 0 : height - this.title.style.height,
        ease: 'quad.inOut',
      })
      .to(
        this.chevron.style,
        {
          duration: instant ? 0 : 0.2,
          r: value ? 0 : Math.PI,
          ease: 'quad.inOut',
        },
        '<',
      );
  }
}

function toast(message: string) {
  const toast = document.createElement('div');
  toast.appendChild(document.createTextNode(message));
  toast.style.cssText = `
    position: fixed;
    bottom: 5%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 8px;
    padding: 8px 12px;
    background: #242424;
    color: #F1F2F3;
    size: 3em;
    opacity: 0;
  `;
  document.body.appendChild(toast);
  return gsap
    .timeline()
    .to(toast.style, {
      duration: 0.2,
      opacity: 0.9,
    })
    .to(toast.style, {
      duration: 0.2,
      opacity: 0,
      delay: 1.5,
      onComplete: () => {
        toast.remove();
      },
    });
}
